/** @typedef {import('@components/cards/shared/types/charts.types').DonutDataset} DonutDataset */

import React from "react";

import { AreaChart, Metric, DonutChart } from "@tremor/react";

import useOrganisationEventMetrics from "@datahooks/events/useOrganisationEventMetrics";

import { TestableCard, TestableText } from '@components/general/TestableComponents';
import MetricPair from "@components/cards/shared/MetricPair";
import TwoLineDonut from "@components/cards/shared/TwoLineDonut";
import { buildSeverityChartDataFromMetrics, buildEventTypeChartDataFromMetrics } from "@components/cards/events/events.jsx";
import { getEntityColour, AGE_GROUPS, DURATION_GROUPS, formatMinutes } from "@datahooks/metadata/DataUtils";
import { ENABLE_EVENT_SUMMARY_PROJTEST_CHART } from "@home/config";
import { ErrorCard, CARD_TYPES } from "@components/cards/ErrorCard";
import LoadingIcon from "@components/navigation/LoadingIcon";
import logError from "@datahooks/metadata/ErrorLogging.js";

import PropTypes from "prop-types";

export default function OrganisationEventMetrics({ event_level = 'Project' }) {
  const { metrics, isLoading, error } = useOrganisationEventMetrics(event_level);

  const cardLayout = event_level === 'Project' ? 'grid grid-cols-1 lg:grid-cols-4 gap-4' : 'grid grid-cols-1 lg:grid-cols-3 gap-4';

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center p-4 mt-10 gap-3">
        <LoadingIcon showText />
      </div>
    );
  }

  if (error) {
    logError(error);
    return (
      <div className="flex flex-col justify-center items-center p-4 mt-10 gap-3">
        <TestableText dataTestId="oec-metrics-loading-error-header" variant='h4'>Something went wrong!</TestableText>
        <ErrorCard cardType={CARD_TYPES.ERROR} />
        <TestableText dataTestId="oec-metrics-loading-error-message" variant='h6'>Failed to load event metrics.</TestableText>
      </div>
    );
  }

  const basicCounts = metrics?.basic_counts || {};
  const severityBreakdown = metrics?.severity_breakdown || [];
  const eventTypes = metrics?.event_types || [];
  const topProjects = metrics?.top_projects || [];

  const ageBreakdown = metrics?.age_breakdown || [];
  const durationBreakdown = metrics?.duration_breakdown || [];

  // Open/Closed donut data
  /** @type {DonutDataset} */
  const statusData = [
    { name: "Open", count: basicCounts.open_events || 0 },
    { name: "Closed", count: basicCounts.closed_events || 0 }
  ];

  const eventTypeData = buildEventTypeChartDataFromMetrics(eventTypes);

  const severityData = buildSeverityChartDataFromMetrics(severityBreakdown);
 
  const projectsData = topProjects.map(project => ({
    name: project.project_name,
    count: project.event_count,
    color: getEntityColour(project.project_name, project.project_id)
  }));

  const ageData = ageBreakdown.map(item => {
    const group = AGE_GROUPS[item.age_group];
    return {
      name: group?.label || item.age_group,  // Fallback to raw label
      count: item.count,
      color: group?.color || 'gray'
    };
  });

  const durationData = durationBreakdown.map(item => {
    const group = DURATION_GROUPS[item.duration_group];

    return {
      name: group?.label || item.duration_group,  // Fallback to raw label
      count: item.count,
      color: group?.color || 'gray'
    };
  });

  let projectTestTimeline = [];
  let customTooltip = null;

  if (ENABLE_EVENT_SUMMARY_PROJTEST_CHART && event_level === 'Project') {

    function transformCorrelationData(correlationData, days = 30) {
      if (!correlationData || correlationData.length === 0) return [];
      
      // Create maps for aggregated counts
      const projectMap = new Map();
      const testMap = new Map();
      
      correlationData.forEach(item => {
        const dateKey = item.event_date;
        
        if (item.event_level === 'Project') {
          projectMap.set(dateKey, (projectMap.get(dateKey) || 0) + item.daily_count);
        } else if (item.event_level === 'Test') {
          testMap.set(dateKey, (testMap.get(dateKey) || 0) + item.daily_count);
        }
      });
        
      // Fill all days in the last 30 days
      const result = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        result.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          "Project Events": projectMap.get(dateStr) || 0,
          "Test Events": testMap.get(dateStr) || 0
        });
      }
        
        return result;
    }

    // Helper function to get detailed events for a specific date
    function getEventsForDate(displayDate, correlationData) {
      // Convert display date back to ISO format for matching
      const targetDate = new Date(displayDate + ' 2025').toISOString().split('T')[0];
      
      const events = correlationData.filter(item => item.event_date === targetDate);
      
      return {
        project: events.filter(e => e.event_level === 'Project'),
        test: events.filter(e => e.event_level === 'Test')
      };
    }
    
    // Custom tooltip component
    customTooltip = ({ active, payload, label }) => {
      if (!active || !payload || payload.length === 0) return null;

      const dateEvents = getEventsForDate(label, metrics.project_test_correlation);
        
      return (
        <div className="bg-white p-4 border border-gray-200 shadow-lg rounded-lg max-w-sm">
          <p className="font-semibold text-gray-900 mb-3">{label}</p>
          
          {/* Project Events */}
          {dateEvents.project.length > 0 && (
            <div className="mb-3">
              <p className="text-blue-600 font-medium mb-1">
                Project Events ({dateEvents.project.reduce((sum, e) => sum + e.daily_count, 0)})
              </p>
              {dateEvents.project.map((event) => (
                <div key={`${event.project_name}_${event.full_event_description}_${event.event_date}`} className="text-sm text-gray-700 ml-2">
                  <span className="font-medium">{event.project_name}:</span> {event.event_type} ({event.daily_count})
                </div>
              ))}
            </div>
          )}

          {/* Test Events */}
          {dateEvents.test.length > 0 && (
            <div>
              <p className="text-purple-600 font-medium mb-1">
                Test Events ({dateEvents.test.reduce((sum, e) => sum + e.daily_count, 0)})
              </p>
              {dateEvents.test.map((event) => (
                <div key={`${event.project_name}_${event.full_event_description}_${event.event_date}`} className="text-sm text-gray-700 ml-2">
                  <span className="font-medium">{event.project_name}:</span> {event.full_event_description} ({event.daily_count})
                </div>
              ))}
            </div>
          )}
          
          {dateEvents.project.length === 0 && dateEvents.test.length === 0 && (
            <p className="text-gray-500 text-sm">No events on this date</p>
          )}
        </div>
      );
    };

    projectTestTimeline = transformCorrelationData(metrics.project_test_correlation);
  }

  return (
    <div className="mb-6 p-6 bg-gray-50 dark:bg-dark-tremor-background-muted rounded-lg border dark:border-dark-tremor-border">
        <TestableText dataTestId="oec-metrics-title" variant="h3" className="mb-4">{`${event_level} Event Metrics`}</TestableText>

            <div className={`${cardLayout}`}>
                <TestableCard dataTestId="oecm-active-events-card" className="p-4">
                    <MetricPair
                        ChartComponent={
                        <TwoLineDonut
                          data-testid="oecm-active-events-donut"
                          data={statusData}
                          colours={['blue', 'gray']}
                          topText={`${basicCounts.total_events}`}
                          bottomText="EVENTS"
                          showAnimation
                          style={{ fontSize: '2rem' }}
                        />}
                        rightSlot={
                            <div className="flex flex-col items-center justify-items-center w-full">
                                <TestableText dataTestId="oecm-api-coverage" variant="h2" className="text-lg mb-2 text-center">Open Events</TestableText>
                                <Metric className="metric-auto-height">{basicCounts?.open_events}</Metric>
                            </div>
                        }
                    />
                </TestableCard>
                <TestableCard dataTestId="oecm-severity-card" className="p-4">
                  <TestableText dataTestId="oecm-severity-title" variant="h2" className="text-gray-600 mb-3">Severity</TestableText>
                  <DonutChart
                    data-testid="oecm-severity-donut"
                    data={severityData}
                    category="count"
                    index="name"
                    colors={severityData.map(d => d.color)}
                    showAnimation
                    className="h-32"
                    style={{ fontSize: '2rem' }}
                  />
                </TestableCard>
                <TestableCard dataTestId="oecm-event-types-card" className="p-4">
                  <TestableText dataTestId="oecm-event-types-title" variant="h2" className="text-gray-600 mb-3">Event Types</TestableText>
                  <DonutChart
                    data-testid="oecm-event-types-donut"
                    data={eventTypeData}
                    category="count"
                    index="name"
                    colors={eventTypeData.map(d => d.color)}
                    showAnimation
                    className="h-32"
                    style={{ fontSize: '2rem' }}
                  />
                </TestableCard>
                <TestableCard dataTestId="oecm-project-counts-card" className="p-4">
                  <TestableText dataTestId="oecm-project-counts-title" variant="h2" className="text-gray-600 mb-3">Projects</TestableText>
                  <DonutChart
                    data-testid="oecm-project-counts-donut"
                    data={projectsData}
                    category="count"
                    index="name"
                    colors={projectsData.map(d => d.color)}
                    showAnimation
                    className="h-32"
                    style={{ fontSize: '2rem' }}
                  />
                </TestableCard>
                {event_level === 'Test' && (
                  <>
                    <TestableCard dataTestId="oecm-open-event-age-card" className="p-4">
                      <TestableText dataTestId="oecm-open-event-age-title" variant="h2" className="text-gray-600 mb-3">Open Event Age</TestableText>
                      <DonutChart
                        data-testid="oecm-open-event-age-donut"
                        data={ageData}
                        category="count"
                        index="name"
                        colors={ageData.map(d => d.color)}
                        showAnimation
                        className="h-32"
                        style={{ fontSize: '2rem' }}
                      />
                    </TestableCard>

                    <TestableCard dataTestId="oecm-open-event-duration-card" className="p-4">
                      <MetricPair
                        ChartComponent={
                        <TwoLineDonut
                          data-testid="oecm-open-event-duration-donut"
                          data={durationData}
                          category="count"
                          index="name"
                          colours={durationData.map(d => d.color)}
                          showAnimation
                          style={{ fontSize: '2rem' }}
                          topText={`${basicCounts.total_events}`}
                          bottomText="EVENTS"
                        />}
                        rightSlot={
                          <div className="flex flex-col items-center justify-items-center w-full text-center">
                            <TestableText dataTestId="oecm-api-coverage" variant="h2" className="text-lg mb-2 text-center">Average Resolution</TestableText>
                            <Metric className="metric-auto-height" style={{ fontSize: '2.125rem'}}>{formatMinutes((metrics?.resolution_metrics?.avg_resolution_hours * 60)) || 0}</Metric>
                          </div>
                        }
                        
                      />
                    </TestableCard>
                  </>
                )}
                
            </div>
            {ENABLE_EVENT_SUMMARY_PROJTEST_CHART && event_level === 'Project' && (
            <div className="mt-6">
              <TestableCard dataTestId="oecm-project-test-correlation-events-card" className="p-4 flex flex-col items-center justify-center">
                <TestableText dataTestId="oecmpt-correlation-events-title" className="text-sm text-gray-600 mb-3">
                  Event Correlation: Project vs Test Events (Last 30 Days)
                </TestableText>
                <AreaChart
                  data-testid="oecmpt-correlation-chart"
                  data={projectTestTimeline}
                  index="date"
                  categories={["Project Events", "Test Events"]}
                  colors={["red", "blue"]}
                  showAnimation
                  showGradient
                  className="h-48"
                  stack={false}
                  customTooltip={customTooltip}
                />
              </TestableCard>
            </div>
            )}

    </div>
  );
}

OrganisationEventMetrics.propTypes = {
  event_level: PropTypes.oneOf(['Project', 'Test']).isRequired
};